import type { OptimizeBasis } from "./optimize-request";

export function buildAnalysisRequestBody(
  startValue: number | null,
  basis: OptimizeBasis,
): Record<string, unknown> {
  const amount = (value: number) =>
    typeof value === "number" && Number.isFinite(value) ? String(value) : "0";
  const body: Record<string, unknown> = {
    scenarios: ["EXPECTED", "LOW"],
    monetary_basis: basis.monetaryBasis,
    include_deposit_fee: basis.includeFees === true,
    include_advisory_fee: basis.includeFees === true,
    include_product_fees: basis.includeFees === true,
    include_financial_plan: false,
    reserved_amount: amount(basis.reservedAmount),
    deposit_amount: amount(basis.depositAmount),
    withdrawal_amount: amount(basis.withdrawalAmount),
    max_resolution_type: "YEAR",
  };
  if (typeof startValue === "number" && Number.isFinite(startValue)) {
    body.start_value = String(startValue);
  }
  return body;
}

export interface DeckTemplate {
  id: string;
  name?: string | null;
}

export function offeredReportTemplates(templates: readonly DeckTemplate[]): ReportTemplate[] {
  return REPORT_BRANDS.flatMap(([brand, name]) => {
    const template = templates.find(
      (candidate) => candidate.name === name && typeof candidate.id === "string" && candidate.id,
    );
    return template ? [{ id: template.id, name, brand }] : [];
  });
}

export function defaultReportTemplate(
  offered: readonly ReportTemplate[],
  firmName: string | null,
): ReportTemplate | null {
  return (
    offered.find((template) => firmBearsName(firmName, template.name)) ??
    offered.find((template) => template.brand === DEFAULT_REPORT_BRAND) ??
    offered[0] ??
    null
  );
}

function firmBearsName(firmName: string | null, templateName: string): boolean {
  if (!firmName) return false;
  const brand = words(templateName.replace(/ Report$/, ""));
  return brand !== "" && ` ${words(firmName)} `.includes(` ${brand} `);
}

function words(text: string): string {
  return text
    .normalize("NFD")
    .replace(/\p{M}/gu, "")
    .toLowerCase()
    .split(/[^a-z0-9]+/)
    .filter(Boolean)
    .join(" ");
}

export interface ReportTemplate {
  id: string;
  name: string;
  brand: ReportBrand;
}

export type ReportBrand = "citroneer" | "tornby" | "xyz";

const REPORT_BRANDS: readonly (readonly [ReportBrand, string])[] = [
  ["citroneer", "Citroneer Report"],
  ["tornby", "Tornby Report"],
  ["xyz", "XYZ Report"],
];

const DEFAULT_REPORT_BRAND: ReportBrand = "citroneer";

export function idFromLocation(location: string | null): string | null {
  if (!location) return null;
  return location.split("/").filter(Boolean).pop() ?? null;
}

export function quantTaskRaceLost(status: number, body: string): boolean {
  const message = messageOf(body);
  if (status === 400) return message === "task not finished";
  return status === 500 && typeof message === "string" && /not finished/i.test(message);
}

export function emptyTransactionSetRefused(status: number, body: string): boolean {
  const message = messageOf(body);
  return (
    status === 400 &&
    typeof message === "string" &&
    message.includes("does not have any transactions")
  );
}

function messageOf(body: string): unknown {
  const start = body.indexOf("{");
  if (start === -1) return undefined;
  try {
    const parsed: unknown = JSON.parse(body.slice(start));
    return typeof parsed === "object" && parsed !== null
      ? (parsed as { message?: unknown }).message
      : undefined;
  } catch {
    return undefined;
  }
}
